import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { ValidationItems } from '@kanda-libs/ks-component-ts';

import { ApplyFormFieldValues, useApplyContext } from 'pages/Apply/context';

import { OMNI_RESTRICTED_EMAIL_ADDRESSES } from '../../../../constants';

export interface ContactInformationFieldsHook {
  emailValidation: ValidationItems;
  phoneValidation: ValidationItems;
  onPhoneBlur: () => void;
}

export default function useContactInformationFields(): ContactInformationFieldsHook {
  const { job } = useApplyContext();

  const [telephone, mobile] = useWatch<
    ApplyFormFieldValues,
    ['credit.customer_details.telephone', 'credit.customer_details.mobile']
  >({
    name: [
      'credit.customer_details.telephone',
      'credit.customer_details.mobile',
    ],
  });

  const { getValues, trigger } = useFormContext<ApplyFormFieldValues>();

  const onPhoneBlur = useCallback(
    () =>
      trigger([
        'credit.customer_details.telephone',
        'credit.customer_details.mobile',
      ]),
    [trigger],
  );

  const emailValidationFunction = useCallback(() => {
    const flowType = job?.flow_type;
    if (flowType === 'example') return true;
    const email = getValues('credit.customer_details.email');
    if (!email) return false;
    const prefix = email?.split('@')[0];
    return !OMNI_RESTRICTED_EMAIL_ADDRESSES.includes(`${prefix}@`);
  }, [getValues, job]);

  const phoneValidation = useMemo(
    () => ({
      validate: {
        value: () => !!telephone || !!mobile,
        message:
          'You must provide either your phone number or your mobile number',
      },
    }),
    [telephone, mobile],
  );

  const emailValidation = useMemo(
    () => ({
      validate: {
        value: () => emailValidationFunction(),
        message: `Email cannot start with "${
          getValues('credit.customer_details.email')?.split('@')[0]
        }"`,
      },
    }),
    [emailValidationFunction, getValues],
  );

  return {
    emailValidation,
    phoneValidation,
    onPhoneBlur,
  };
}
